import { useCallback } from "react";
import { RefOption } from "../../../services/agenda/types";
import { refOptionsActions } from "../actions";
import { IRefOptionsContextService } from "../services";
import { RefOptionsState } from "../types";
import { RefOptionsDispatch } from "./useRefOptionsState";

export function useRefOptionsCache(
  service: IRefOptionsContextService,
  dispatch: RefOptionsDispatch,
  state: RefOptionsState,
) {
  const getOptionsByCode = useCallback(
    async (code: string): Promise<RefOption[]> => {
      const cachedOptions = service.getCachedOptions(code);
      if (cachedOptions) {
        return cachedOptions;
      }

      if (state.loadingOptions[code]) {
        return new Promise((resolve, reject) => {
          const checkLoading = () => {
            if (!state.loadingOptions[code]) {
              const options = service.getCachedOptions(code);
              if (options) {
                resolve(options);
              } else if (state.errors.options[code]) {
                reject(new Error(state.errors.options[code]!));
              }
            } else {
              setTimeout(checkLoading, 100);
            }
          };
          checkLoading();
        });
      }

      dispatch(refOptionsActions.setLoadingOptions(code, true));

      try {
        const options = await service.fetchOptionsByCode(code);
        service.cacheOptions(code, options);
        dispatch(refOptionsActions.setOptions(code, options));
        return options;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Não foi possível carregar as opções do formulário. Atualize a página e tente novamente.";
        dispatch(refOptionsActions.setOptionsError(code, errorMessage));
        throw error;
      }
    },
    [service, dispatch, state.loadingOptions, state.errors.options],
  );

  const clearCache = useCallback(() => {
    service.clearCache();
    dispatch(refOptionsActions.clearCache());
  }, [service, dispatch]);

  return {
    getOptionsByCode,
    clearCache,
  };
}
